import {createSelector, Selector} from 'reselect';
import {PropertyID} from 'module/Properties/enum/PropID';
import {PropertiesStateType} from 'module/Properties/reducer/PropertiesReducer';
import {log} from 'common/log';
import {ViewPosition} from 'module/Properties/enum/ViewPosition'

export const PropLog = {unfetchedUse: false}

type TransformFunction<T, P1 = void, P2 = void, P3 = void> = ((value: string | null, x1: P1, x2: P2, x3: P3) => T)

export function selPropGenString(propID: PropertyID, defaultValue: string | null = null) {
	return (state: PropertiesStateType) => {
		const id = propID.toString()
		const val = state.Properties[id]
		if (state.Properties.hasOwnProperty(id)) {
			if (defaultValue && val == null) return defaultValue;
			return val
		}
		if (PropLog.unfetchedUse) log.warn('Property used, but not fetched: ' + id)
		return defaultValue
	}
}

export function selPropGen<T, P1 = void, P2 = void, P3 = void>(
	propID: PropertyID,
	transform: TransformFunction<T, P1, P2, P3>,
	s1?: Selector<any, P1>,
	s2?: Selector<any, P2>,
	s3?: Selector<any, P3>,
): Selector<PropertiesStateType, T> {
	const selectors: Selector<any, any>[] = [selPropGenString(propID)]
	if (s1) selectors.push(s1)
	if (s2) selectors.push(s2)
	if (s3) selectors.push(s3)
	return createSelector(selectors, transform)
}

export function selVisiblePropertyGen(
	propID: PropertyID,
	viewPosition: ViewPosition,
	defaultValue?: boolean, // only default position as default visible when not present (most frequent case = 'J')
) {
	const dflt = (typeof defaultValue !== 'boolean') ? viewPosition === ViewPosition.Default : defaultValue
	if (viewPosition === ViewPosition.Any) {
		return selPropGen(propID, (p) => Boolean(p ? p !== "N" : defaultValue))
	}
	return createSelector(
		selPropGenString(propID, dflt ? viewPosition : 'N'),
		(p: null | undefined | string) => !!p && p.includes(viewPosition),
	)
}

function transformViewPositionPerAppinstProperty(p: string | null) {
	if (!p) return []
	const ret = p.split(/[|]/)
		.flatMap(x => {
				const separatorIndex = x.indexOf('*')
				if (separatorIndex < 1) return [{propValue: x, propAppInst: ''}]
				const propValue = x.substring(0, separatorIndex)
				let propAppInst: string = x.substring(separatorIndex + 1)
				propAppInst = propAppInst?.trim() || ''
				return {propValue, propAppInst}
			},
		)
		.sort((a, b) => a.propAppInst.localeCompare(b.propAppInst))
	return ret
}

export function selVisibleAppinstListPropertyGen(
	propID: PropertyID,
	viewPosition: ViewPosition,
	defaultValue?: boolean, // only default position as default visible when not present (most frequent case = 'J')
) {
	const dflt = (typeof defaultValue !== 'boolean') ? viewPosition === ViewPosition.Default : defaultValue
	return createSelector(
		selPropGen(propID, transformViewPositionPerAppinstProperty),
		(p) => {
			if (!p.length && dflt) return [viewPosition]
			return p.filter(x => x.propValue.includes(viewPosition)).map(x => x.propAppInst || null)
		},
	)
}


const selDict: { [propID in string]: Selector<PropertiesStateType, any> } = {}

/**
 * use only when transform function does not change
 */
export function selPropGenMemo<T>(propID: PropertyID, transform: TransformFunction<T>): Selector<PropertiesStateType, T> {
	const propIDstr = propID.toString()
	let sel: Selector<PropertiesStateType, T> = selDict[propIDstr]
	if (!sel) {
		sel = selDict[propIDstr] = selPropGen(propID, transform)
	}
	return sel
}